// components/EventDetailView.js — read-only event detail display

import React from 'react';
import { Calendar, Clock, MapPin, Tag, Palette, Timer } from 'lucide-react';
import { COLOR_CATEGORIES } from '../constants/colors';

function formatDate(event) {
  if (!event?.year || !event?.month || !event?.day) return null;
  const d = new Date(event.year, event.month - 1, event.day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(event) {
  if (!event?.time) return null;
  if (event.time === 'All Day') return 'All Day';
  const end = event.endTime ? formatEndTime(event.endTime) : null;
  return end ? `${event.time} – ${end}` : event.time;
}

function formatEndTime(time24) {
  if (!time24) return null;
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function getColorLabel(dotColor) {
  const cat = COLOR_CATEGORIES.find(c => c.color === dotColor);
  return cat ? cat.label : null;
}

function getPriorityLabel(tags) {
  if (!tags || tags.length === 0) return null;
  return tags.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ');
}

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start p-4 border-b border-gray-100 last:border-b-0">
      <div className="w-8 pt-0.5">
        <Icon size={18} className="text-gray-500" />
      </div>
      <div className="flex-1">
        <div className="text-xs font-medium text-gray-500 mb-0.5">{label}</div>
        <div className="text-base text-gray-900">{value}</div>
      </div>
    </div>
  );
}

export default function EventDetailView({ event }) {
  if (!event) return null;

  // Extract Tailwind color classes from event.color string (e.g. "bg-blue-100 border-blue-400")
  const colorClasses = typeof event.color === 'string' ? event.color : 'bg-blue-100 border-blue-400';

  return (
    <div className="flex-1 overflow-y-auto p-4">
      {/* Title with color indicator */}
      <div className={`border-l-4 rounded-lg p-4 mb-3 ${colorClasses}`}>
        <h2 className="text-xl font-bold text-gray-900">{event.title || '(No title)'}</h2>
        {event.source === 'google' && (
          <span className="text-xs text-gray-500 mt-1 block">Google Calendar</span>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <DetailRow icon={Calendar} label="Date" value={formatDate(event)} />
        <DetailRow icon={Clock} label="Time" value={formatTime(event)} />
        <DetailRow icon={MapPin} label="Location" value={event.location} />
        <DetailRow icon={Timer} label="Prep Time" value={event.needsPrep ? 'Yes' : null} />
        <DetailRow icon={Palette} label="Category" value={getColorLabel(event.dotColor)} />
        <DetailRow icon={Tag} label="Priority" value={getPriorityLabel(event.tags)} />
      </div>
    </div>
  );
}
